// ============================================================================
// pollOverlay.ts
//
// Sondages en direct (2026-10-15) : le bandeau du sondage sur l'image diffusée.
//
// Le panneau Sondages (PollsDock) tient `PollOverlay` à jour dans une ref à
// chaque relevé ; la boucle de composition (useCanvasPipeline) la lit à chaque
// image et appelle drawPollOverlay, après les incrustations et avant le
// filigrane. Rien n'est dessiné quand la ref est vide : la régie ne montre le
// bandeau que si l'opérateur a choisi « Afficher à l'antenne ».
//
// Maquette : canevas « Sondages en direct — Kleon », planche 5 (À l'antenne).
// Carte en bas à droite, 470 px de large sur une image de 1280 × 720 ; tout est
// proportionnel à la hauteur de l'image. Avec le filigrane « Boosté par Kleon.TV »
// (même coin), la carte monte au-dessus de lui.
// ============================================================================

export interface PollOverlayOption {
  label   : string;
  /** Part des voix, de 0 à 100. */
  pct     : number;
  /** Bonne réponse d'un quiz, montrée seulement une fois le sondage clos. */
  correct : boolean;
  /** Réponse en tête d'un sondage clos (doré). */
  winner  : boolean;
}

export interface PollOverlay {
  pollId       : number;
  question     : string;
  quiz         : boolean;
  phase        : 'open' | 'closed';
  /** Faux tant que le sondage est ouvert aux résultats cachés : les réponses seules. */
  showPercents : boolean;
  options      : PollOverlayOption[];
  totalVotes   : number;
  /** Fin du vote (Date.now()), pour le minuteur ; null = sans limite. */
  deadline     : number | null;
  /** Première apparition (Date.now()), pour le fondu d'entrée. */
  shownAt      : number;
}

const FONT = `-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif`;

const VIOLET      = '#8c5cf5';
const VIOLET_TEXT = '#b9a2ff';
const GOLD        = '#f5b14c';
const GREEN       = '#5fd39a';

/** Parts affichées, qui glissent vers les vraies à chaque image (les barres ne sautent pas). */
const animated = new Map<number, number[]>();

const countdown = (deadline: number, now: number): string => {
  const seconds = Math.max(0, Math.ceil((deadline - now) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

const formatVotes = (n: number): string =>
  `${n.toLocaleString('fr-FR')} vote${n > 1 ? 's' : ''}`;

/** Coupe le texte à la largeur, avec des points de suspension. */
const fit = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string => {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let cut = text;
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > maxWidth) cut = cut.slice(0, -1);
  return `${cut.trimEnd()}…`;
};

/** Découpe la question en lignes (3 au plus, la dernière tronquée). */
const wrap = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] => {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (let i = 0; i < words.length; i++) {
    const attempt = line ? `${line} ${words[i]}` : words[i];
    if (ctx.measureText(attempt).width <= maxWidth || !line) { line = attempt; continue; }
    lines.push(line);
    line = words[i];
    if (lines.length === maxLines - 1) { line = words.slice(i).join(' '); break; }
  }
  if (line) lines.push(lines.length === maxLines - 1 ? fit(ctx, line, maxWidth) : line);
  return lines.slice(0, maxLines);
};

const setSpacing = (ctx: CanvasRenderingContext2D, px: number) => {
  // `letterSpacing` n'existe pas partout (Firefox ancien) : sans lui, le texte reste lisible.
  const c = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in c) c.letterSpacing = `${px}px`;
};

/**
 * Dessine le bandeau du sondage. `bottomReserve` : hauteur (en px du canevas)
 * à laisser libre en bas à droite, celle du filigrane quand il est affiché.
 */
export function drawPollOverlay(
  ctx: CanvasRenderingContext2D, w: number, h: number, poll: PollOverlay, bottomReserve: number,
): void {
  const now = Date.now();
  const s   = h / 720;

  let previous = animated.get(poll.pollId);
  if (!previous || previous.length !== poll.options.length) {
    // Un seul sondage à la fois : les anciens ne servent plus.
    animated.clear();
    previous = poll.options.map(() => 0);
  }
  const values = previous.map((v, i) => v + (poll.options[i].pct - v) * 0.12);
  animated.set(poll.pollId, values);

  const cardW  = Math.min(470 * s, w - 96 * s);
  const padX   = 24 * s;
  const innerW = cardW - padX * 2;
  const barH   = 44 * s;
  const barGap = 12 * s;
  const gap    = 12 * s;

  ctx.save();
  ctx.font = `800 ${23 * s}px ${FONT}`;
  const questionLines = wrap(ctx, poll.question, innerW, 3);
  const questionLineH = 23 * s * 1.25;

  const headerH = 20 * s;
  const footerH = 20 * s;
  const cardH   = 6 * s + 20 * s + headerH + gap + questionLines.length * questionLineH + gap
                + poll.options.length * barH + (poll.options.length - 1) * barGap + gap + footerH + 22 * s;

  const x = w - 48 * s - cardW;
  const y = h - Math.max(48 * s, bottomReserve + 16 * s) - cardH;

  ctx.globalAlpha = Math.min(1, (now - poll.shownAt) / 300);

  // La carte et son liseré violet.
  ctx.beginPath();
  ctx.roundRect(x, y, cardW, cardH, 18 * s);
  ctx.fillStyle = 'rgba(10, 9, 24, 0.92)';
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.fillStyle = VIOLET;
  ctx.fillRect(x, y, cardW, 6 * s);
  ctx.restore();

  let cy = y + 6 * s + 20 * s;

  // En-tête : SONDAGE EN DIRECT … 1:42
  const closed = poll.phase === 'closed';
  const label  = closed
    ? (poll.quiz ? 'QUIZ · LA RÉPONSE' : 'RÉSULTAT DU SONDAGE')
    : (poll.quiz ? 'QUIZ EN DIRECT' : 'SONDAGE EN DIRECT');
  ctx.textBaseline = 'middle';
  ctx.textAlign    = 'left';
  ctx.font         = `800 ${14 * s}px ${FONT}`;
  ctx.fillStyle    = closed && poll.quiz ? GREEN : VIOLET_TEXT;
  setSpacing(ctx, 1.4 * s);
  ctx.fillText(label, x + padX, cy + headerH / 2);
  setSpacing(ctx, 0);

  const corner = closed ? formatVotes(poll.totalVotes) : poll.deadline !== null ? countdown(poll.deadline, now) : '';
  if (corner) {
    ctx.textAlign = 'right';
    ctx.font      = `700 ${16 * s}px ${FONT}`;
    ctx.fillStyle = '#ffffff';
    ctx.fillText(corner, x + cardW - padX, cy + headerH / 2);
  }
  cy += headerH + gap;

  // La question.
  ctx.textAlign    = 'left';
  ctx.textBaseline = 'top';
  ctx.font         = `800 ${23 * s}px ${FONT}`;
  ctx.fillStyle    = '#ffffff';
  for (const line of questionLines) {
    ctx.fillText(line, x + padX, cy);
    cy += questionLineH;
  }
  cy += gap;

  // Les réponses.
  poll.options.forEach((option, i) => {
    const highlight = closed && (poll.quiz ? option.correct : option.winner);
    const accent    = poll.quiz ? GREEN : GOLD;
    const bx = x + padX, by = cy;

    ctx.beginPath();
    ctx.roundRect(bx, by, innerW, barH, 10 * s);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.fill();

    if (poll.showPercents) {
      ctx.save();
      ctx.clip();
      ctx.fillStyle = highlight
        ? (poll.quiz ? 'rgba(95, 211, 154, 0.45)' : 'rgba(245, 177, 76, 0.55)')
        : 'rgba(140, 92, 245, 0.5)';
      ctx.fillRect(bx, by, innerW * Math.max(0, Math.min(100, values[i])) / 100, barH);
      ctx.restore();
    }

    if (highlight) {
      ctx.lineWidth   = 2 * s;
      ctx.strokeStyle = accent;
      ctx.beginPath();
      ctx.roundRect(bx + s, by + s, innerW - 2 * s, barH - 2 * s, 9 * s);
      ctx.stroke();
    }

    const pctText = poll.showPercents ? `${Math.round(option.pct)} %` : '';
    ctx.textBaseline = 'middle';
    ctx.font         = `800 ${18 * s}px ${FONT}`;
    const pctW       = pctText ? ctx.measureText(pctText).width + 12 * s : 0;

    let lx = bx + 14 * s;
    if (highlight && poll.quiz) {
      // La coche de la bonne réponse.
      ctx.strokeStyle = GREEN;
      ctx.lineWidth   = 3 * s;
      ctx.lineCap     = 'round';
      ctx.lineJoin    = 'round';
      ctx.beginPath();
      ctx.moveTo(lx,          by + barH / 2);
      ctx.lineTo(lx + 5 * s,  by + barH / 2 + 5 * s);
      ctx.lineTo(lx + 15 * s, by + barH / 2 - 6 * s);
      ctx.stroke();
      lx += 24 * s;
    }

    ctx.textAlign = 'left';
    ctx.font      = `${highlight ? 700 : 600} ${18 * s}px ${FONT}`;
    ctx.fillStyle = '#ffffff';
    ctx.fillText(fit(ctx, option.label, bx + innerW - 14 * s - pctW - lx), lx, by + barH / 2);

    if (pctText) {
      ctx.textAlign = 'right';
      ctx.font      = `800 ${18 * s}px ${FONT}`;
      ctx.fillText(pctText, bx + innerW - 14 * s, by + barH / 2);
    }

    cy += barH + barGap;
  });
  cy += gap - barGap;

  // Pied : invitation à voter, ou remerciement.
  ctx.textBaseline = 'middle';
  ctx.font         = `400 ${16 * s}px ${FONT}`;
  ctx.fillStyle    = 'rgba(255, 255, 255, 0.85)';
  ctx.textAlign    = 'left';
  ctx.fillText(closed ? 'Merci pour vos votes !' : 'Votez maintenant sur', x + padX, cy + footerH / 2);
  ctx.textAlign = 'right';
  ctx.font      = `800 ${16 * s}px ${FONT}`;
  ctx.fillStyle = '#ffffff';
  ctx.fillText('kleon.tv', x + cardW - padX, cy + footerH / 2);

  ctx.restore();
}

/**
 * Hauteur occupée par le filigrane « Boosté par Kleon.TV » en bas à droite
 * (cf. drawWatermark de useCanvasPipeline : marge 3 %, logo 3 × 3,4 %, marges
 * intérieures 2 × 1,2 % de la hauteur).
 */
export const watermarkReserve = (h: number): number =>
  Math.round(h * 0.03) + Math.round(h * 0.034) * 3 + Math.round(h * 0.012) * 2;
